import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum';
import { LinkTypeEnum } from '@empathybroker/tsclient-play/dist/enums/link-type.enum';
import { PlayErrorReasonEnum } from '@empathybroker/tsclient-play/dist/enums/play-error-reason.enum';
import { PromotionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/promotion-type.enum';
import { RankingActionEnum } from '@empathybroker/tsclient-play/dist/enums/ranking-action.enum';

import { CatalogFieldTypeEnum, FacetSortTypeEnum, LocaleEnum, PermissionLevelEnum, ResultMappingIdEnum, RoleEnum } from '../enums';

import { Dictionary, ToolsMessages } from '../models';

/* tslint:disable:max-line-length */

const DEFAULT_FIELDS_MESSAGES_EN = {
  creationDate: {
    label: 'Created on'
  },
  updateDate: {
    label: 'Updated on'
  },
  user: {
    label: 'Updated by'
  },
  enabled: {
    label: 'Enabled',
    states: {
      enabled: 'Enabled',
      disabled: 'Disabled'
    }
  },
  schedule: {
    label: 'Scheduled',
    states: {
      schedule: 'Scheduled',
      activationDate: 'From {date}',
      deactivationDate: 'to {date}',
      always: 'Always active'
    }
  },
  placeholders: {
    always: 'Always',
    scheduled: 'Select a date',
    keywords: {
      active: 'Add words separated by \';\' and press the "enter" key',
      disabled: 'Queries addition is disabled for this link'
    }
  },
  activationDate: {
    label: 'Active from'
  },
  deactivationDate: {
    label: 'Active to'
  }
};

const CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN = 'changes will be applied on the next catalogue indexing cycle';

const TOOLS_MESSAGES_EN: ToolsMessages = {
  equalize: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      title: {
        label: 'Name'
      },
      notes: {
        label: 'Notes',
        actions: {
          show: 'Show notes',
          hide: 'Hide notes'
        }
      },
      enabledTime: {
        label: 'Enabled time'
      },
      enabled: {
        ...DEFAULT_FIELDS_MESSAGES_EN.enabled,
        alert: 'Enabling this equalize will change the current Search configuration'
      },
      search: {
        label: 'Search products to preview',
        help: 'Search a term to preview the equalize configuration result'
      }
    }),
    products: {
      countInfo: 'Showing {counter} results'
    },
    noDataFound: 'No equalize found',
    dataFiltered: 'Equalize are currently filtered',
    create: {
      success: 'Equalize created successfully'
    },
    update: {
      success: 'Equalize edited successfully'
    },
    delete: {
      confirmTitle: 'Delete equalize',
      confirmMessage: 'Are you sure you want to delete the selected equalize ({itemsCount})?',
      success: 'Equalize deleted successfully'
    },
    enable: {
      confirmTitle: 'Enable equalize',
      confirmMessage: 'This action will disable the current equalize, are you sure you want to do it?',
      success: 'Equalize enabled successfully'
    },
    disable: {
      success: 'Equalize disabled successfully'
    },
    details: 'Details',
    configuration: 'Configuration',
    fieldValues: 'Fields',
    preview: 'Preview',
    enabledTimePeriods: {
      year: {
        singular: 'year',
        plural: 'years'
      },
      month: {
        singular: 'month',
        plural: 'months'
      },
      day: {
        singular: 'day',
        plural: 'days'
      },
      hour: {
        singular: 'hour',
        plural: 'hours'
      },
      lessThan: 'Less than',
      zero: 'None'
    }
  },
  synonyms: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      hyponyms: {
        empty: 'Empty',
        label: 'One way Synonyms',
        help: 'Each of these terms will return the same as its equivalent synonyms',
        valuesCountInfo: '{counter} hyponyms added'
      },
      synonyms: {
        label: 'Equivalent Synonyms',
        help: 'All these terms will mean the same for the Search engine',
        valuesCountInfo: '{counter} synonyms added',
        errors: {
          [PlayErrorReasonEnum.DUPLICATED]: 'Conflicting values ({values}) on synonym [{synonym}]'
        }
      },
      type: {
        label: 'Type',
        values: {
          color: 'Color',
          basic: 'Basic'
        }
      }
    }),
    noValues: 'No synonyms added yet',
    return: 'Returns also',
    noDataFound: 'No synonyms found',
    dataFiltered: 'Synonyms are currently filtered',
    create: {
      success: `Synonym created successfully; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN }`
    },
    update: {
      success: `Synonym edited successfully; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN }`
    },
    delete: {
      confirmTitle: 'Delete synonyms',
      confirmMessage: 'Are you sure you want to delete the selected synonyms ({itemsCount})?',
      success: `Synonyms deleted successfully; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN }`
    },
    enable: {
      success: `Synonyms enabled successfully; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN }`
    },
    disable: {
      success: `Synonyms disabled successfully; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN }`
    },
    conflicts: {
      disable: {
        success: `Conflicting synonym disabled successfully; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN }`
      },
      delete: {
        confirmTitle: 'Delete synonym',
        confirmMessage: 'Are you sure you want to delete the conflicting synonym?',
        success: `Conflicting synonym deleted successfully; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN }`
      }
    }
  },
  blacklist: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      lang: {
        label: 'Language',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]: 'There is already a blacklist enabled for this language'
        }
      },
      terms: {
        label: 'Terms',
        info: 'List of terms associated with the blacklist'
      }
    }),
    noDataFound: 'No blacklists found',
    dataFiltered: 'Blacklists are currently filtered',
    create: {
      success: 'Blacklist created successfully'
    },
    update: {
      success: 'Blacklist edited successfully'
    },
    delete: {
      confirmTitle: 'Delete blacklists',
      confirmMessage: 'Are you sure you want to delete the selected blacklists ({itemsCount})',
      success: 'Blacklists deleted successfully'
    },
    enable: {
      success: 'Blacklists enabled successfully'
    },
    disable: {
      success: 'Blacklists disabled successfully'
    }
  },
  promotions: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      type: {
        label: 'Type',
        values: {
          [PromotionTypeEnum.BANNER]: 'Banner',
          [PromotionTypeEnum.PROMOTED]: 'Promoted'
        }
      },
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the promotion is shown',
        valuesCountInfo: '{counter} queries added'
      },
      matchAll: {
        label: 'Match all queries',
        value: 'All queries',
        help: 'This option will be applied to all searches of your site'
      },
      url: {
        label: 'URL'
      },
      imageUrl: {
        label: 'Image URL',
        previewLabel: 'Image preview'
      },
      title: {
        label: 'Title'
      },
      weight: {
        label: 'Priority',
        help: 'Promotions will appear in order of importance (most to least), being 9 the most and 0 the least'
      }
    }),
    noDataFound: 'No promotions found',
    dataFiltered: 'Promotions are currently filtered',
    create: {
      success: 'Promotion created successfully'
    },
    update: {
      success: 'Promotion edited successfully'
    },
    delete: {
      confirmTitle: 'Delete promotions',
      confirmMessage: 'Are you sure you want to delete the selected promotions ({itemsCount})?',
      success: 'Promotions deleted successfully'
    },
    enable: {
      success: 'Promotions enabled successfully'
    },
    disable: {
      success: 'Promotions disabled successfully'
    }
  },
  redirections: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the redirection is shown',
        valuesCountInfo: '{counter} queries added'
      },
      url: {
        label: 'URL'
      }
    }),
    noDataFound: 'No redirections found',
    dataFiltered: 'Redirections are currently filtered',
    create: {
      success: 'Redirection created successfully'
    },
    update: {
      success: 'Redirection edited successfully'
    },
    delete: {
      confirmTitle: 'Delete redirections',
      confirmMessage: 'Are you sure you want to delete the selected redirections ({itemsCount})?',
      success: 'Redirections deleted successfully'
    },
    enable: {
      success: 'Redirections enabled successfully'
    },
    disable: {
      success: 'Redirections disabled successfully'
    }
  },
  links: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      type: {
        label: 'Type',
        values: {
          [LinkTypeEnum.DIRECT]: 'Direct link',
          [LinkTypeEnum.BANNER]: 'Banner',
          [LinkTypeEnum.PROMOTED]: 'Promoted'
        }
      },
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the link is shown',
        valuesCountInfo: '{counter} queries added'
      },
      matchAll: {
        label: 'Match all queries',
        value: 'All queries',
        help: 'This option will be applied to all searches of your site'
      },
      url: {
        label: 'URL'
      },
      imageUrl: {
        label: 'Image',
        uploadLabel: 'Upload image',
        previewLabel: 'Image preview'
      },
      title: {
        label: 'Title'
      },
      weight: {
        label: 'Priority',
        help: 'Links will appear in order of importance (most to least), being 9 the most and 0 the least'
      }
    }),
    noDataFound: 'No links found',
    dataFiltered: 'Links are currently filtered',
    create: {
      success: 'Link created successfully'
    },
    update: {
      success: 'Link edited successfully'
    },
    delete: {
      confirmTitle: 'Delete links',
      confirmMessage: 'Are you sure you want to delete the selected links ({itemsCount})?',
      success: 'Links deleted successfully'
    },
    enable: {
      success: 'Links enabled successfully'
    },
    disable: {
      success: 'Links disabled successfully'
    }
  },
  productsRanking: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      lang: {
        label: 'Language'
      },
      keywords: {
        label: 'Queries',
        help: 'List of search terms in which the rank is applied',
        valuesCountInfo: '{counter} queries added'
      },
      products: {
        label: 'Products'
      },
      search: {
        label: 'Search Products to rank',
        labelFiltered: 'Search Products to rank in',
        help: 'Search a product using a query or an ID'
      },
      order: {
        label: 'Order'
      },
      fieldValue: {
        label: 'ID / SKU'
      },
      name: {
        label: 'Product Name'
      },
      imageUrl: {
        label: 'Image'
      },
      filters: {
        label: 'Filters'
      }
    }),
    products: {
      title: 'Products',
      titleEdition: 'Edit your selection',
      titleList: 'Ranked List',
      titleSearch: 'Search by products',
      titlePreview: 'Preview your ranking',
      titleProductPreview: 'Preview',
      countInfo: 'Showing {counter} results',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      item: {
        [RankingActionEnum.BOOST]: {
          select: 'Boost',
          selected: 'Boosted'
        },
        [RankingActionEnum.BURY]: {
          select: 'Bury',
          selected: 'Buried'
        },
        preview: 'Product preview'
      },
      elementsSelectedCountInfo: '{counter} elements selected'
    },
    noDataFound: 'No products found',
    dataFiltered: 'Products are currently filtered',
    create: {
      success: 'Product ranking created successfully'
    },
    update: {
      success: 'Product ranking edited successfully'
    },
    delete: {
      confirmTitle: 'Delete Product ranking',
      confirmMessage: 'Are you sure you want to delete the selected products ({itemsCount})?',
      success: 'Product ranking deleted successfully'
    },
    enable: {
      success: 'Product rankings enabled successfully'
    },
    disable: {
      success: 'Product rankings disabled successfully'
    }
  },
  attributesRanking: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      lang: {
        label: 'Language'
      },
      keywords: {
        label: 'Queries',
        help: 'List of search terms in which the rank is applied',
        valuesCountInfo: '{counter} queries added'
      },
      search: {
        label: 'Search attributes to rank'
      },
      scope: {
        label: 'Scope'
      },
      filters: {
        label: 'Filters'
      },
      fieldName: {
        label: 'Field'
      },
      fieldValue: {
        label: 'Value'
      }
    }),
    entry: {
      add: 'Add',
      edit: 'Edit',
      titleEdition: 'Edit your selection'
    },
    preview: {
      title: 'Preview'
    },
    products: {
      countInfo: 'Showing {counter} results'
    },
    attributes: {
      label: 'Attributes',
      item: {
        [RankingActionEnum.BOOST]: {
          select: 'Boost',
          selected: 'Boosted'
        },
        [RankingActionEnum.BURY]: {
          select: 'Bury',
          selected: 'Buried'
        }
      }
    },
    noDataFound: 'No attributes found',
    dataFiltered: 'Attributes are currently filtered',
    create: {
      success: 'Attribute ranking created successfully'
    },
    update: {
      success: 'Attribute ranking edited successfully'
    },
    delete: {
      confirmTitle: 'Delete Attribute ranking',
      confirmMessage: 'Are you sure you want to delete the selected attributes ({itemsCount})?',
      success: 'Attribute ranking deleted successfully'
    },
    enable: {
      success: 'Attribute rankings enabled successfully'
    },
    disable: {
      success: 'Attribute rankings disabled successfully'
    }
  },
  boosts: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the boost is applied',
        valuesCountInfo: '{counter} queries added'
      },
      products: {
        label: 'Products',
        search: 'Search',
        help: '{count} selected. Products will be shown in order'
      }
    }),
    noDataFound: 'No boosts found',
    dataFiltered: 'Boosts are currently filtered',
    create: {
      success: 'Boost created successfully'
    },
    update: {
      success: 'Boost edited successfully'
    },
    delete: {
      confirmTitle: 'Delete boosts',
      confirmMessage: 'Are you sure you want to delete the selected boosts ({itemsCount})?',
      success: 'Boosts deleted successfully'
    },
    enable: {
      success: 'Boosts enabled successfully'
    },
    disable: {
      success: 'Boosts disabled successfully'
    }
  },
  configurations: {
    sections: {
      details: 'Details',
      settings: 'Settings',
      preview: 'Preview'
    },
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      name: {
        label: 'Name'
      },
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the configuration is applied'
      },
      features: {
        label: 'Modified',
        attributes: 'attributes',
        products: 'products',
        facets: 'facets'
      },
      facets: {
        label: 'Facets',
        info: 'Select the group of facets and values to be visible on the products listing page.',
        edit: 'Select facets',
        id: {
          label: 'Facet'
        },
        prettyName: {
          label: 'Name'
        },
        sortType: {
          label: 'sort values',
          inlineLabel: 'sorted',
          values: {
            default: 'by default',
            [FacetSortTypeEnum.ALPHABETICALLY]: 'alphabetically',
            [FacetSortTypeEnum.RESULTS_NUMBER]: 'by results count'
          }
        },
        values: {
          empty: 'You haven\'t configured any value yet.'
        },
        actions: {
          add: 'Add Facet',
          preview: 'Show facets'
        },
        emptyList: 'You haven\'t configured any facet yet.'
      },
      attributes: {
        label: 'Attributes',
        info: 'Boost and bury specific values of your catalog attributes.',
        edit: 'Select attributes',
        fieldName: 'Attribute',
        fieldValue: 'Value',
        actions: {
          bury: 'Bury',
          boost: 'Boost'
        },
        emptyList: 'You haven\'t configured any attribute yet.'
      },
      products: {
        label: 'Products',
        info: 'Select directly in the preview the products you want to boost or bury. They\'ll be listed here.',
        emptyList: 'You haven\'t configured any product yet.',
        search: {
          tag: 'Query',
          query: 'Search products',
          help: 'Search a product using a query or an ID'
        },
        notFound: 'No products found',
        actions: {
          bury: 'Bury',
          boost: 'Boost',
          unbury: 'Unbury',
          unboost: 'Unboost'
        }
      },
      inheritance: {
        label: 'Inheritance',
        inputLabel: 'Apply configuration to child items',
        states: {
          enabled: 'Yes',
          disabled: 'No'
        }
      }
    }),
    noDataFound: 'No configurations found',
    dataFiltered: 'Configurations are currently filtered',
    create: {
      success: 'Configuration created successfully'
    },
    update: {
      success: 'Configuration edited successfully'
    },
    delete: {
      confirmTitle: 'Delete configurations',
      confirmMessage: 'Are you sure you want to delete the selected configurations ({itemsCount})?',
      success: 'Configurations deleted successfully'
    },
    enable: {
      success: 'Configurations enabled successfully'
    },
    disable: {
      success: 'Configurations disabled successfully'
    },
    back: 'Back to {view}',
    attributes: {
      names: {
        category: 'categories',
        promosIds: 'promotions',
        groupsIds: 'content groups'
      },
      noDataFound: 'No {attributeName} found'
    },
    configurations: 'configurations'
  },
  users: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      username: {
        label: 'Username'
      },
      password: {
        label: 'Password',
        error: 'Password must include letters, numbers and be at least {minLength} characters long'
      },
      repeatPassword: {
        label: 'Repeat Password',
        error: 'Passwords do not match'
      },
      email: {
        label: 'Email'
      },
      fullName: {
        label: 'Full name'
      },
      firstName: {
        label: 'First name'
      },
      lastName: {
        label: 'Last name'
      },
      roles: {
        label: 'Roles',
        values: {
          [RoleEnum.SEARCH_ANALYST]: {
            label: 'Search Analyst',
            description: 'It allows the user to view reporting data'
          },
          [RoleEnum.SEARCH_ADMIN]: {
            label: 'Search Admin',
            description: 'It allows the user to manage the whole instance where this role is granted'
          },
          [RoleEnum.SEARCH_MANAGER]: {
            label: 'Search Manager',
            description: 'It allows the user to manage links, products ranking and other kind of entities'
          },
          [RoleEnum.EB_APP]: {
            label: 'Applications',
            description: 'Specific role for application accounts'
          }
        },
        error: 'Choose at least 1 role'
      }
    }),
    noDataFound: 'No users found',
    dataFiltered: 'Users are currently filtered',
    create: {
      success: 'User created successfully'
    },
    update: {
      success: 'User edited successfully'
    },
    delete: {
      confirmTitle: 'Delete users',
      confirmMessage: 'Are you sure you want to delete the selected users ({itemsCount})?',
      success: 'Users deleted successfully'
    },
    enable: {
      success: 'Users enabled successfully'
    },
    disable: {
      success: 'Users disabled successfully'
    }
  },
  instances: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      instanceId: {
        label: 'ID'
      },
      prettyName: {
        label: 'Label'
      },
      imagotype: {
        label: 'Imagotype'
      },
      isotype: {
        label: 'Isotype'
      },
      permissions: {
        label: 'Permissions',
        feature: {
          label: 'Feature'
        },
        level: {
          label: 'Permission',
          values: {
            [PermissionLevelEnum.NONE]: 'None',
            [PermissionLevelEnum.PREVIEW]: 'Preview',
            [PermissionLevelEnum.READ]: 'Read',
            [PermissionLevelEnum.WRITE]: 'Write'
          },
          remove: 'Remove configuration'
        }
      },
      kpis: {
        label: 'KPIs',
        fields: {
          id: {
            label: 'KPI'
          }
        }
      },
      catalog: {
        label: 'Catalog',
        fields: {
          title: 'Fields',
          id: {
            label: 'Id'
          },
          prettyName: {
            label: 'Label'
          },
          types: {
            [CatalogFieldTypeEnum.SEARCHABLE]: 'Searchable',
            [CatalogFieldTypeEnum.RANKABLE]: 'Rankable',
            [CatalogFieldTypeEnum.FACETABLE]: 'Facetable'
          },
          actions: {
            add: 'Add field',
            edit: 'Edit field',
            confirm: 'Confirm changes',
            delete: 'Delete field'
          },
          noFieldsAddedYet: 'No fields added yet'
        },
        resultMappings: {
          title: 'Result mappings',
          [ResultMappingIdEnum.ID]: {
            label: 'ID'
          },
          [ResultMappingIdEnum.NAME]: {
            label: 'Name'
          },
          [ResultMappingIdEnum.URL]: {
            label: 'Url'
          },
          [ResultMappingIdEnum.URL_PREFIX]: {
            label: 'Url prefix'
          },
          [ResultMappingIdEnum.IMAGE_URL]: {
            label: 'Image url'
          },
          [ResultMappingIdEnum.IMAGE_URL_PREFIX]: {
            label: 'Image url prefix'
          },
          none: 'None'
        },
        index: {
          title: 'Index',
          token: 'Token'
        }
      },
      tools: {
        label: 'Tools',
        base: {
          label: 'Base',
          fields: {
            pivots: {
              label: 'Pivots'
            }
          }
        },
        [ActionTypeEnum.SYNONYM]: {
          label: 'Synonym',
          fields: {
            types: {
              label: 'Types',
              id: {
                label: 'Id'
              },
              prettyName: {
                label: 'Label'
              },
              actions: {
                add: 'Add type',
                edit: 'Edit type',
                confirm: 'Confirm changes',
                delete: 'Delete type'
              },
              noTypesAddedYet: 'No types added yet'
            }
          }
        },
        [ActionTypeEnum.CONFIGURATION]: {
          label: 'Configuration',
          fields: {
            attributeNames: {
              label: 'Attributes'
            }
          }
        }
      }
    }),
    noDataFound: 'No instances found',
    dataFiltered: 'Instances are currently filtered',
    create: {
      success: 'Instance created successfully'
    },
    update: {
      success: 'Instance edited successfully'
    },
    delete: {
      confirmTitle: 'Delete instances',
      confirmMessage: 'Are you sure you want to delete the selected instances ({itemsCount})?',
      success: 'Instances deleted successfully'
    },
    enable: {
      success: 'Instances enabled successfully'
    },
    disable: {
      success: 'Instances disabled successfully'
    },
    instance: 'Instance'
  },
  index: {
    submitButton: 'Submit',
    submitTitle: 'Submit a feed',
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      feedId: {
        label: 'Feed Id'
      },
      date: {
        label: 'Date'
      },
      id: {
        label: 'Job ID'
      },
      fileId: {
        label: 'File Id'
      },
      timestamps: {
        label: 'Times (ms)'
      },
      elements: {
        label: 'Elements'
      },
      state: {
        label: 'State'
      },
      metrics: {
        read: 'R',
        write: 'W',
        total: 'T'
      },
      file: {
        label: 'File',
        placeholder: 'Select a file'
      }
    }),
    noDataFound: 'No jobs found',
    submit: {
      success: 'Feed file submitted successfully'
    }
  }
};

const DEFAULT_FIELDS_MESSAGES_ES = {
  creationDate: {
    label: 'Creado el'
  },
  updateDate: {
    label: 'Actualizado el'
  },
  user: {
    label: 'Actualizado por'
  },
  enabled: {
    label: 'Habilitado',
    states: {
      enabled: 'Habilitado',
      disabled: 'Deshabilitado'
    }
  },
  schedule: {
    label: 'Programado',
    states: {
      schedule: 'Programado',
      activationDate: 'Desde el {date}',
      deactivationDate: 'Hasta el {date}',
      always: 'Siempre activo'
    }
  },
  placeholders: {
    always: 'Siempre',
    scheduled: 'Selecciona una fecha',
    keywords: {
      active: 'Añade palabras separadas por \';\' y presiona la tecla "intro"',
      disabled: 'Las queries están desactivadas para este link'
    }
  },
  activationDate: {
    label: 'Activo desde'
  },
  deactivationDate: {
    label: 'Activo hasta'
  }
};

const CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES = 'los cambios serán aplicados en el siguiente ciclo de indexación del catálogo';

const TOOLS_MESSAGES_ES: ToolsMessages = {
  equalize: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      title: {
        label: 'Nombre'
      },
      notes: {
        label: 'Notas',
        actions: {
          show: 'Mostrar notas',
          hide: 'Ocultar notas'
        }
      },
      enabledTime: {
        label: 'Tiempo habilitado'
      },
      enabled: {
        ...DEFAULT_FIELDS_MESSAGES_ES.enabled,
        alert: 'Habilitar este equalize cambiará la configuración actual del buscador'
      },
      search: {
        label: 'Busca productos para previsualizar',
        help: 'Busca un término para visualizar los resultados de la configuración del equalize'
      }
    }),
    products: {
      countInfo: 'Mostrando {counter} resultados'
    },
    noDataFound: 'No se han encontrado equalize',
    dataFiltered: 'Los equalize están siendo filtrados',
    create: {
      success: 'Equalize creado con éxito'
    },
    update: {
      success: 'Equalize editado con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar equalize',
      confirmMessage: '¿Estás seguro de que quieres eliminar los equalize seleccionados ({itemsCount})?',
      success: 'Equalize eliminados con éxito'
    },
    enable: {
      confirmTitle: 'Habilitar equalize',
      confirmMessage: 'Esta acción deshabilitará el equalize actual, ¿estás seguro de que quieres hacerlo?',
      success: 'Equalize habilitado con éxito'
    },
    disable: {
      success: 'Equalize deshabilitado con éxito'
    },
    details: 'Detalles',
    configuration: 'Configuración',
    fieldValues: 'Campos',
    preview: 'Vista previa',
    enabledTimePeriods: {
      year: {
        singular: 'año',
        plural: 'años'
      },
      month: {
        singular: 'mes',
        plural: 'meses'
      },
      day: {
        singular: 'día',
        plural: 'días'
      },
      hour: {
        singular: 'hora',
        plural: 'horas'
      },
      lessThan: 'Menos de',
      zero: 'Ninguno'
    }
  },
  synonyms: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      hyponyms: {
        empty: 'Vacío',
        label: 'Hipónimos',
        help: 'Cada uno de estos términos devolverá lo mismo que sus sinónimos equivalentes',
        valuesCountInfo: '{counter} hipónimos añadidos'
      },
      synonyms: {
        label: 'Sinónimos',
        help: 'Todos estos términos significarán lo mismo para el motor de búsqueda',
        valuesCountInfo: '{counter} sinónimos añadidos',
        errors: {
          [PlayErrorReasonEnum.DUPLICATED]: 'Valores en conflicto ({values}) con el sinónimo [{synonym}]'
        }
      },
      type: {
        label: 'Tipo',
        values: {
          color: 'Color',
          basic: 'Básico'
        }
      }
    }),
    noValues: 'Aún no se han añadidos sinónimos',
    return: 'También devuelve',
    noDataFound: 'No se han encontrado sinónimos',
    dataFiltered: 'Los sinónimos están siendo filtrados',
    create: {
      success: `Sinónimo creado con éxito; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES }`
    },
    update: {
      success: `Sinónimo editado con éxito; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES }`
    },
    delete: {
      confirmTitle: 'Eliminar sinónimos',
      confirmMessage: '¿Estás seguro de que quieres eliminar los sinónimos seleccionados ({itemsCount})?',
      success: `Sinónimos eliminados con éxito; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES }`
    },
    enable: {
      success: `Sinónimos habilitados con éxito; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES }`
    },
    disable: {
      success: `Sinónimos deshabilitados con éxito; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES }`
    },
    conflicts: {
      disable: {
        success: `Sinónimo en conflicto deshabilitado con éxito; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES }`
      },
      delete: {
        confirmTitle: 'Eliminar sinónimo',
        confirmMessage: '¿Estás seguro de que quieres eliminar el sinónimo en conflicto?',
        success: `Sinónimo en conflicto eliminado con éxito; ${ CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES }`
      }
    }
  },
  blacklist: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      lang: {
        label: 'Idioma',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]: 'Ya existe una blacklist habilitada para este idioma'
        }
      },
      terms: {
        label: 'Términos',
        info: 'Lista de términos asociados a la blacklist'
      }
    }),
    noDataFound: 'No se han encontrado blacklists',
    dataFiltered: 'Las blacklists están siendo filtradas',
    create: {
      success: 'Blacklist creada con éxito'
    },
    update: {
      success: 'Blacklist editada con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar blacklists',
      confirmMessage: '¿Estás seguro de que quieres eliminar las blacklists seleccionadas ({itemsCount})?',
      success: 'Blacklists eliminadas con éxito'
    },
    enable: {
      success: 'Blacklists habilitadas con éxito'
    },
    disable: {
      success: 'Blacklists deshabilitadas con éxito'
    }
  },
  promotions: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      type: {
        label: 'Tipo',
        values: {
          [PromotionTypeEnum.BANNER]: 'Banner',
          [PromotionTypeEnum.PROMOTED]: 'Promoted'
        }
      },
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que la promotion se muestra',
        valuesCountInfo: '{counter} queries añadidas'
      },
      matchAll: {
        label: 'Mostrar en todas las búsquedas',
        value: 'Todas las búsquedas',
        help: 'Esta opción será aplicada a todas las búsquedas del sitio'
      },
      url: {
        label: 'URL'
      },
      imageUrl: {
        label: 'URL de la imagen',
        previewLabel: 'Vista previa de la imagen'
      },
      title: {
        label: 'Título'
      },
      weight: {
        label: 'Prioridad',
        help: 'Las promotions aparecerán en orden de importancia (de mayor a menor), siendo 9 el máximo y 0 el mínimo'
      }
    }),
    noDataFound: 'No se han encontrado promotions',
    dataFiltered: 'Las promotions están siendo filtradas',
    create: {
      success: 'Promotion creada con éxito'
    },
    update: {
      success: 'Promotion editada con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar promotions',
      confirmMessage: '¿Estás seguro de que quieres eliminar las promotions seleccionadas ({itemsCount})?',
      success: 'Promotions eliminadas con éxito'
    },
    enable: {
      success: 'Promotions habilitadas con éxito'
    },
    disable: {
      success: 'Promotions deshabilitadas con éxito'
    }
  },
  redirections: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que la redirection se muestra',
        valuesCountInfo: '{counter} queries añadidas'
      },
      url: {
        label: 'URL'
      }
    }),
    noDataFound: 'No se han encontrado redirections',
    dataFiltered: 'Las redirections están siendo filtradas',
    create: {
      success: 'Redirection creada con éxito'
    },
    update: {
      success: 'Redirection editada con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar redirections',
      confirmMessage: '¿Estás seguro de que quieres eliminar las redirections seleccionadas ({itemsCount})?',
      success: 'Redirections eliminadas con éxito'
    },
    enable: {
      success: 'Redirections habilitadas con éxito'
    },
    disable: {
      success: 'Redirections deshabilitadas con éxito'
    }
  },
  links: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      type: {
        label: 'Tipo',
        values: {
          [LinkTypeEnum.DIRECT]: 'Direct link',
          [LinkTypeEnum.BANNER]: 'Banner',
          [LinkTypeEnum.PROMOTED]: 'Promoted'
        }
      },
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que el link se muestra',
        valuesCountInfo: '{counter} queries añadidas'
      },
      matchAll: {
        label: 'Mostrar en todas las búsquedas',
        value: 'Todas las búsquedas',
        help: 'Esta opción será aplicada a todas las búsquedas del sitio'
      },
      url: {
        label: 'URL'
      },
      imageUrl: {
        label: 'Imagen',
        uploadLabel: 'Subir una imagen',
        previewLabel: 'Vista previa de la imagen'
      },
      title: {
        label: 'Título'
      },
      weight: {
        label: 'Prioridad',
        help: 'Los links aparecerán en orden de importancia (de mayor a menor), siendo 9 el máximo y 0 el mínimo'
      }
    }),
    noDataFound: 'No se han encontrado links',
    dataFiltered: 'Los links están siendo filtrados',
    create: {
      success: 'Link creado con éxito'
    },
    update: {
      success: 'Link editado con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar links',
      confirmMessage: '¿Estás seguro de que quieres eliminar los links seleccionados ({itemsCount})?',
      success: 'Links eliminados con éxito'
    },
    enable: {
      success: 'Links habilitados con éxito'
    },
    disable: {
      success: 'Links deshabilitados con éxito'
    }
  },
  productsRanking: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      lang: {
        label: 'Idioma'
      },
      keywords: {
        label: 'Queries',
        help: 'Lista de términos de búsqueda para los que el ranking es aplicado',
        valuesCountInfo: '{counter} queries añadidas'
      },
      products: {
        label: 'Productos'
      },
      search: {
        label: 'Busca productos para ordenar',
        labelFiltered: 'Busca productos para ordenar en',
        help: 'Busca un producto usando una query o un ID'
      },
      order: {
        label: 'Orden'
      },
      fieldValue: {
        label: 'ID / SKU'
      },
      name: {
        label: 'Nombre del producto'
      },
      imageUrl: {
        label: 'Imagen'
      },
      filters: {
        label: 'Filtros'
      }
    }),
    products: {
      title: 'Productos',
      titleEdition: 'Edita tu seleccion',
      titleList: 'Lista de ranking',
      titleSearch: 'Buscar por producto',
      titlePreview: 'Ver tus rankings',
      titleProductPreview: 'Vista previa',
      countInfo: 'Mostrando {counter} resultados',
      selectAll: 'Seleccionar todo',
      deselectAll: 'Deseleccionar todo',
      item: {
        [RankingActionEnum.BOOST]: {
          select: 'Boost',
          selected: 'Boosted'
        },
        [RankingActionEnum.BURY]: {
          select: 'Bury',
          selected: 'Buried'
        },
        preview: 'Vista previa del producto'
      },
      elementsSelectedCountInfo: '{counter} elementos seleccionados'
    },
    noDataFound: 'No se han encontrado product rankings',
    dataFiltered: 'Los product rankings están siendo filtrados',
    create: {
      success: 'Product ranking creado con éxito'
    },
    update: {
      success: 'Product ranking editado con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar product ranking',
      confirmMessage: '¿Estás seguro de que quieres eliminar los product ranking seleccionados ({itemsCount})?',
      success: 'Product rankings eliminados con éxito'
    },
    enable: {
      success: 'Product rankings habilitados con éxito'
    },
    disable: {
      success: 'Product rankings deshabilitados con éxito'
    }
  },
  attributesRanking: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      lang: {
        label: 'Idioma'
      },
      keywords: {
        label: 'Queries',
        help: 'Lista de términos de búsqueda para los que el boost es aplicado',
        valuesCountInfo: '{counter} queries añadidas'
      },
      search: {
        label: 'Busca productos para boostear'
      },
      scope: {
        label: 'Alcance'
      },
      filters: {
        label: 'Filtros',
        actions: {
          edit: 'Editar'
        }
      },
      fieldName: {
        label: 'Campo'
      },
      fieldValue: {
        label: 'Valor'
      }
    }),
    entry: {
      add: 'Añadir',
      edit: 'Editar',
      titleEdition: 'Edita tu selección'
    },
    preview: {
      title: 'Vista previa'
    },
    products: {
      countInfo: 'Mostrando {counter} resultados'
    },
    attributes: {
      label: 'Atributos',
      item: {
        [RankingActionEnum.BOOST]: {
          select: 'Boost',
          selected: 'Boosted'
        },
        [RankingActionEnum.BURY]: {
          select: 'Bury',
          selected: 'Buried'
        }
      }
    },
    noDataFound: 'No se han encontrado attribute rankings',
    dataFiltered: 'Los attribute rankings están siendo filtrados',
    create: {
      success: 'Attribute ranking creado con éxito'
    },
    update: {
      success: 'Attribute ranking editado con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar attribute ranking',
      confirmMessage: '¿Estás seguro de que quieres eliminar los attribute rankings seleccionados ({itemsCount})?',
      success: 'Attribute rankings eliminados con éxito'
    },
    enable: {
      success: 'Attribute rankings habilitados con éxito'
    },
    disable: {
      success: 'Attribute rankings deshabilitados con éxito'
    }
  },
  boosts: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que el boost es aplicado',
        valuesCountInfo: '{counter} queries añadidas'
      },
      products: {
        label: 'Productos',
        search: 'Buscar',
        help: '{count} seleccionados. Los productos se mostrarán en orden'
      }
    }),
    noDataFound: 'No se han encontrado boosts',
    dataFiltered: 'Los boosts están siendo filtrados',
    create: {
      success: 'Boost creado con éxito'
    },
    update: {
      success: 'Boost editado con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar boosts',
      confirmMessage: '¿Estás seguro de que quieres eliminar los boosts seleccionados ({itemsCount})?',
      success: 'Boosts eliminados con éxito'
    },
    enable: {
      success: 'Boosts habilitados con éxito'
    },
    disable: {
      success: 'Boosts deshabilitados con éxito'
    }
  },
  configurations: {
    sections: {
      details: 'Detalles',
      settings: 'Configuración',
      preview: 'Vista previa'
    },
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      name: {
        label: 'Nombre'
      },
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que la configuración se aplica'
      },
      features: {
        label: 'Modificado',
        attributes: 'atributos',
        products: 'productos',
        facets: 'facets'
      },
      facets: {
        label: 'Facets',
        info: 'Selecciona el grupo de facets y valores a mostrar en la página de listado de productos.',
        edit: 'Seleccionar facets',
        id: {
          label: 'Facet'
        },
        prettyName: {
          label: 'Nombre'
        },
        sortType: {
          label: 'ordenar valores',
          inlineLabel: 'ordenado',
          values: {
            default: 'por defecto',
            [FacetSortTypeEnum.ALPHABETICALLY]: 'alfabéticamente',
            [FacetSortTypeEnum.RESULTS_NUMBER]: 'por resultados'
          }
        },
        values: {
          empty: 'No has configurado ningún valor aún.'
        },
        actions: {
          add: 'Añadir Facet',
          preview: 'Mostrar facets'
        },
        emptyList: 'No has configurado ningún facet aún.'
      },
      attributes: {
        label: 'Atributos',
        info: 'Haz boost o bury a ciertos valores de atributos de tu catálogo.',
        edit: 'Seleccionar atributos',
        fieldName: 'Atributo',
        fieldValue: 'Valor',
        actions: {
          bury: 'Bury',
          boost: 'Boost'
        },
        emptyList: 'No has configurado ningún atributo aún.'
      },
      products: {
        label: 'Productos',
        info: 'Selecciona en la vista previa los productos a los que quieras hacer boost o bury. Aparecerán listados aquí.',
        emptyList: 'No has configurado ningún producto aún.',
        search: {
          tag: 'Query',
          query: 'Buscar productos',
          help: 'Busca un producto usando una query o un ID'
        },
        notFound: 'No se han encontrado productos',
        actions: {
          bury: 'Bury',
          boost: 'Boost',
          unbury: 'Unbury',
          unboost: 'Unboost'
        }
      },
      inheritance: {
        label: 'Herencia',
        inputLabel: 'Aplicar configuración a los elementos hijos',
        states: {
          enabled: 'Sí',
          disabled: 'No'
        }
      }
    }),
    noDataFound: 'No se han encontrado configuraciones',
    dataFiltered: 'Las configuraciones están siendo filtradas',
    create: {
      success: 'Configuración creada con éxito'
    },
    update: {
      success: 'Configuración editada con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar configuraciones',
      confirmMessage: '¿Estás seguro de que quieres eliminar las configuraciones seleccionadas ({itemsCount})?',
      success: 'Configuraciones eliminadas con éxito'
    },
    enable: {
      success: 'Configuraciones habilitadas con éxito'
    },
    disable: {
      success: 'Configuraciones deshabilitadas con éxito'
    },
    back: 'Volver a {view}',
    attributes: {
      names: {
        category: 'categorías',
        promosIds: 'promociones',
        groupsIds: 'content groups'
      },
      noDataFound: 'No se han encontrado {attributeName}'
    },
    configurations: 'configuraciones'
  },
  users: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      username: {
        label: 'Usuario'
      },
      password: {
        label: 'Contraseña',
        error: 'La contraseña debe incluir letras, números y tener al menos una longitud de {minLength} caracteres'
      },
      repeatPassword: {
        label: 'Repite la contraseña',
        error: 'Las contraseñas no coinciden'
      },
      email: {
        label: 'Email'
      },
      fullName: {
        label: 'Nombre'
      },
      firstName: {
        label: 'Nombre'
      },
      lastName: {
        label: 'Apellidos'
      },
      roles: {
        label: 'Roles',
        values: {
          [RoleEnum.SEARCH_ANALYST]: {
            label: 'Search Analyst',
            description: 'Permite al usuario ver la sección de analítica'
          },
          [RoleEnum.SEARCH_ADMIN]: {
            label: 'Search Admin',
            description: 'Permite al usuario gestionar la instancia completa'
          },
          [RoleEnum.SEARCH_MANAGER]: {
            label: 'Search Manager',
            description: 'Permite al usuario gestionar links, products ranking y otros tipos de entidades'
          },
          [RoleEnum.EB_APP]: {
            label: 'Applications',
            description: 'Rol específico para cuentas de aplicación'
          }
        },
        error: 'Elige al menos un rol'
      }
    }),
    noDataFound: 'No se han encontrado usuarios',
    dataFiltered: 'Los usuarios están siendo filtrados',
    create: {
      success: 'Usuario creado con éxito'
    },
    update: {
      success: 'Usuario editado con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar usuarios',
      confirmMessage: '¿Estás seguro de que quieres eliminar los usuarios seleccionados ({itemsCount})?',
      success: 'Usuarios eliminados con éxito'
    },
    enable: {
      success: 'Usuarios habilitados con éxito'
    },
    disable: {
      success: 'Usuarios deshabilitados con éxito'
    }
  },
  instances: {
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      instanceId: {
        label: 'ID'
      },
      prettyName: {
        label: 'Etiqueta'
      },
      imagotype: {
        label: 'Imagotipo'
      },
      isotype: {
        label: 'Isotipo'
      },
      permissions: {
        label: 'Permisos',
        feature: {
          label: 'Feature'
        },
        level: {
          label: 'Permiso',
          values: {
            [PermissionLevelEnum.NONE]: 'Ninguno',
            [PermissionLevelEnum.PREVIEW]: 'Vista previa',
            [PermissionLevelEnum.READ]: 'Lectura',
            [PermissionLevelEnum.WRITE]: 'Escritura'
          },
          remove: 'Eliminar configuración'
        }
      },
      kpis: {
        label: 'KPIs',
        fields: {
          id: {
            label: 'KPI'
          }
        }
      },
      catalog: {
        label: 'Catálogo',
        fields: {
          title: 'Campos',
          id: {
            label: 'Id'
          },
          prettyName: {
            label: 'Etiqueta'
          },
          types: {
            [CatalogFieldTypeEnum.SEARCHABLE]: 'Searchable',
            [CatalogFieldTypeEnum.RANKABLE]: 'Rankable',
            [CatalogFieldTypeEnum.FACETABLE]: 'Facetable'
          },
          actions: {
            add: 'Añadir campo',
            edit: 'Editar campo',
            confirm: 'Confirmar cambios',
            delete: 'Borrar campo'
          },
          noFieldsAddedYet: 'Aún no se han añadido campos'
        },
        resultMappings: {
          title: 'Mapeos del resultado',
          [ResultMappingIdEnum.ID]: {
            label: 'ID'
          },
          [ResultMappingIdEnum.NAME]: {
            label: 'Nombre'
          },
          [ResultMappingIdEnum.URL]: {
            label: 'Url'
          },
          [ResultMappingIdEnum.URL_PREFIX]: {
            label: 'Prefijo de la url'
          },
          [ResultMappingIdEnum.IMAGE_URL]: {
            label: 'Url de la imagen'
          },
          [ResultMappingIdEnum.IMAGE_URL_PREFIX]: {
            label: 'Prefijo de la imagen'
          },
          none: 'Ninguno'
        },
        index: {
          title: 'Index',
          token: 'Token'
        }
      },
      tools: {
        label: 'Herramientas',
        base: {
          label: 'Base',
          fields: {
            pivots: {
              label: 'Pivotes'
            }
          }
        },
        [ActionTypeEnum.SYNONYM]: {
          label: 'Synonym',
          fields: {
            types: {
              label: 'Tipos',
              id: {
                label: 'Id'
              },
              prettyName: {
                label: 'Etiqueta'
              },
              actions: {
                add: 'Añadir tipo',
                edit: 'Editar tipo',
                confirm: 'Confirmar cambios',
                delete: 'Borrar tipo'
              },
              noTypesAddedYet: 'Aún no se han añadido tipos'
            }
          }
        },
        [ActionTypeEnum.CONFIGURATION]: {
          label: 'Configuration',
          fields: {
            attributeNames: {
              label: 'Atributos'
            }
          }
        }
      }
    }),
    noDataFound: 'No se han encontrado instancias',
    dataFiltered: 'Las instancias están siendo filtrados',
    create: {
      success: 'Instancia creada con éxito'
    },
    update: {
      success: 'Instancia editada con éxito'
    },
    delete: {
      confirmTitle: 'Eliminar instancias',
      confirmMessage: '¿Estás seguro de que quieres eliminar las instancias seleccionadas ({itemsCount})?',
      success: 'Instancias eliminadas con éxito'
    },
    enable: {
      success: 'Instancias habilitadas con éxito'
    },
    disable: {
      success: 'Instancias deshabilitadas con éxito'
    },
    instance: 'Instancia'
  },
  index: {
    submitButton: 'Enviar',
    submitTitle: 'Envia un feed',
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      feedId: {
        label: 'ID del Feed'
      },
      date: {
        label: 'Fecha'
      },
      id: {
        label: 'ID del Job'
      },
      fileId: {
        label: 'ID del archivo'
      },
      timestamps: {
        label: 'Tiempos (ms)'
      },
      elements: {
        label: 'Elementos'
      },
      state: {
        label: 'Estado'
      },
      metrics: {
        read: 'L',
        write: 'E',
        total: 'T'
      },
      file: {
        label: 'Archivo',
        placeholder: 'Selecciona un archivo'
      }
    }),
    noDataFound: 'No se han encontrado jobs',
    submit: {
      success: 'Feed enviado correctamente'
    }
  }
};

/* tslint:enable:max-line-length */

export const TOOLS_MESSAGES: Dictionary<{ tools: ToolsMessages }> = {
  [LocaleEnum.ENGLISH]: {
    tools: TOOLS_MESSAGES_EN
  },
  [LocaleEnum.SPANISH]: {
    tools: TOOLS_MESSAGES_ES
  }
};
